<template>
  <div class="main-panel">
    <section>
      <h1>Reporting</h1>

      <div>
        <div class="time-period-section">
          <div>Summary</div>
        </div>

        <div class="date-selector">
          <div class="date-selector-grid">
            <div>
              <label for="period">Select Period:</label>
              <select v-model="selectedPeriod" id="period">
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="yearToDate">Year to Date</option>
                <option value="custom">Custom Range</option>
                <option value="allTime">All Time</option>
              </select>
            </div>

            <div>
              <div v-if="selectedPeriod === 'custom'" class="custom-dates">
                <div>
                  <label>Start Date:</label>
                  <input type="date" v-model="customStart" />
                </div>
                <div>
                  <label>End Date:</label>
                  <input type="date" v-model="customEnd" />
                </div>
              </div>
            </div>

            <div>
              <label for="searchCompetition">Search Competition:</label>
              <input
                type="text"
                placeholder="Search Competition"
                id="searchCompetition"
                v-model="searchText"
              />
            </div>

            <div>
              <label for="period">Competitions:</label>
              <select v-model="selectedCompetition" id="period">
                <option
                  v-for="competition in filteredCompetitions"
                  :key="competition.competitionId"
                  :value="competition.competitionId"
                >
                  {{
                    "WSP" +
                    competition.competitionId +
                    " - " +
                    competition.competitionName
                  }}
                </option>
              </select>
            </div>

            <button @click="loadCharts" :disabled="isLoading">Apply</button>
          </div>

          <table class="order-table">
            <thead>
              <tr class="table-header">
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, key) in orderData" :key="key">
                <td>{{ key }}</td>
                <td>{{ value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="report-section">
        <h2>Target Metrics</h2>
        <div class="grid">
          <div>
            <canvas ref="paidUsersLineChart"></canvas>
          </div>

          <div>
            <canvas ref="valuePerUserLineChart"></canvas>
          </div>
        </div>

        <h2>Tickets &amp; Users</h2>
        <div class="grid">
          <div>
            <canvas ref="userBarChart"></canvas>
          </div>

          <div>
            <canvas ref="ticketsBarChart"></canvas>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from "vue";
import { Chart, registerables } from "chart.js";
import { TitleOptions } from "chart.js";
import ReportingService from "../services/reporting-service";
import competitionService from "../services/competition-service";
import CompetitionDropdownResponse from "../responses/CompetitionsDropdownResponse";
import { formatDateForReporting } from "../utils/datetime-helper";

Chart.register(...registerables);

export default defineComponent({
  setup() {
    const pieChartRef = ref(null);

    const isLoading = ref(false);

    const competitions = ref<CompetitionDropdownResponse[]>([]);
    const selectedCompetition = ref<number>(0);

    const paidUsersLineChart = ref(null);
    const valuePerUserLineChart = ref(null);
    const userBarChart = ref(null);
    const ticketsBarChart = ref(null);

    const paidUsersChartInstance = ref<Chart | null>(null);
    const valuePerUserChartInstance = ref<Chart | null>(null);
    const userBarChartInstance = ref<Chart | null>(null);
    const ticketsBarChartInstance = ref<Chart | null>(null);

    const orderData = ref<Record<string, string> | undefined>(undefined);

    const selectedPeriod = ref("last7");
    const customStart = ref("");
    const customEnd = ref("");
    const searchText = ref("");

    const allCompetitionsOptionValue = 0;

    const getCompetitions = async () => {
      try {
        const response = await competitionService.getAllCompetitions();
        competitions.value = [
          {
            competitionId: allCompetitionsOptionValue,
            competitionName: "All Competitions",
            startDate: "",
          },
          ...response,
        ];
      } catch (error) {
        console.error("Error fetching competitions:", error);
      }
    };

    const chartOptions = (
      title: string,
      chartType: "bar" | "line" | "pie" | "doughnut" = "bar",
      useGBP: boolean = false,
      showLegend: boolean = false,
    ) => {
      const isCircular = chartType === "pie" || chartType === "doughnut";

      return {
        plugins: {
          title: {
            display: true,
            text: title,
          },
          legend: {
            display: showLegend,
          },
        },
        scales: isCircular
          ? undefined
          : {
              y: {
                ticks: {
                  callback: (value: number | string) => {
                    const num =
                      typeof value === "string" ? parseFloat(value) : value;
                    return useGBP
                      ? new Intl.NumberFormat("en-GB", {
                          style: "currency",
                          currency: "GBP",
                          minimumFractionDigits: 2,
                        }).format(num)
                      : `${num}`;
                  },
                },
              },
            },
      };
    };

    const generateBarColors = (count: number): string[] => {
      const colors = [
        "#36a2eb",
        "#ff6384",
        "#4bc0c0",
        "#9966ff",
        "#ff9f40",
        "#f67019",
        "#00a950",
        "#c9cbcf",
        "#ffcd56",
        "#7e57c2",
      ];
      // Repeat colors if needed
      return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
    };

    const getDateRange = (): { startDate: string; endDate: string } => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      let start: Date;
      let end: Date = new Date(tomorrow);

      switch (selectedPeriod.value) {
        case "today":
          start = new Date(today);
          break;

        case "yesterday":
          start = new Date(today);
          start.setDate(start.getDate() - 1);
          end = new Date(today);
          break;

        case "last7":
          start = new Date(today);
          start.setDate(start.getDate() - 6);
          break;

        case "thisMonth":
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          break;

        case "lastMonth":
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 1);
          break;

        case "yearToDate":
          start = new Date(today.getFullYear(), 0, 1);
          break;

        case "custom":
          const customStartDate = new Date(customStart.value);
          const customEndDate = new Date(customEnd.value);
          customEndDate.setDate(customEndDate.getDate() + 1);
          return {
            startDate: formatDateForReporting(customStartDate),
            endDate: formatDateForReporting(customEndDate),
          };
        case "allTime":
          start = getCompetitonStartDate();
          break;

        default:
          start = new Date(today);
          start.setDate(start.getDate() - 6);
          break;
      }

      return {
        startDate: formatDateForReporting(start),
        endDate: formatDateForReporting(end),
      };
    };

    const loadCharts = async () => {
      isLoading.value = true;
      try {
        const { startDate, endDate } = getDateRange();
        console.log(startDate, endDate);

        const data = await ReportingService.getReportingData(
          selectedCompetition.value,
          startDate,
          endDate,
        );

        destroyAllCharts();

        orderData.value = data?.orderData;

        if (paidUsersLineChart.value) {
          paidUsersChartInstance.value = new Chart(paidUsersLineChart.value, {
            type: "line",
            data: {
              labels: data.paidUserLabels,
              datasets: [
                {
                  label: "User Count",
                  data: data.paidUserCount,
                  borderColor: "#36a2eb",
                  fill: false,
                },
              ],
            },
            options: chartOptions("100k Paid Users"),
          });
        }

        if (valuePerUserLineChart.value) {
          valuePerUserChartInstance.value = new Chart(
            valuePerUserLineChart.value,
            {
              type: "line",
              data: {
                labels: data.paidUserLabels,
                datasets: [
                  {
                    label: "Value",
                    data: data.paidUserValues,
                    borderColor: "#36a2eb",
                    fill: false,
                  },
                ],
              },
              options: chartOptions("Value per User", "line", true),
            },
          );
        }

        if (userBarChart.value) {
          userBarChartInstance.value = new Chart(userBarChart.value, {
            type: "bar",
            data: {
              labels: data.userLabels,
              datasets: [
                {
                  label: "Users",
                  data: data.userValues,
                  backgroundColor: generateBarColors(data.userValues.length),
                },
              ],
            },
            options: chartOptions("User Report"),
          });
        }

        if (ticketsBarChart.value) {
          ticketsBarChartInstance.value = new Chart(ticketsBarChart.value, {
            type: "bar",
            data: {
              labels: data.ticketLabels,
              datasets: [
                {
                  data: data.ticketValues,
                  backgroundColor: generateBarColors(data.ticketValues.length),
                },
              ],
            },
            options: chartOptions("Ticket Report"),
          });
        }
      } finally {
        isLoading.value = false;
      }
    };

    const destroyAllCharts = () => {
      if (paidUsersChartInstance.value) {
        paidUsersChartInstance.value.destroy();
        paidUsersChartInstance.value = null;
      }
      if (valuePerUserChartInstance.value) {
        valuePerUserChartInstance.value.destroy();
        valuePerUserChartInstance.value = null;
      }
      if (userBarChartInstance.value) {
        userBarChartInstance.value.destroy();
        userBarChartInstance.value = null;
      }
      if (ticketsBarChartInstance.value) {
        ticketsBarChartInstance.value.destroy();
        ticketsBarChartInstance.value = null;
      }
    };

    const getCompetitonStartDate = () => {
      const selectedComptitionId = selectedCompetition.value;

      if (selectedComptitionId == 0) {
        const validCompetitions = competitions.value.filter((c) => c.startDate);
        const sorted = validCompetitions
          .slice()
          .sort(
            (a, b) =>
              new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
          );
        return new Date(sorted[0].startDate);
      }

      const competition = competitions.value.find(
        (c) => c.competitionId === selectedComptitionId,
      );
      return competition ? new Date(competition.startDate) : new Date(0);
    };

    const filteredCompetitions = computed(() => {
      if (!searchText.value.trim()) return competitions.value;

      const term = searchText.value.toLowerCase();
      return competitions.value.filter((competition) => {
        const displayText =
          `WSP${competition.competitionId} - ${competition.competitionName}`.toLowerCase();
        return displayText.includes(term);
      });
    });

    watch(filteredCompetitions, (newFilteredItems) => {
      if (newFilteredItems.length > 0) {
        selectedCompetition.value = newFilteredItems[0].competitionId;
      } else {
        selectedCompetition.value = 0;
      }
    });

    onMounted(() => {
      loadCharts();
      getCompetitions();
    });

    return {
      isLoading,
      competitions,
      selectedCompetition,
      ticketsBarChart,
      paidUsersLineChart,
      valuePerUserLineChart,
      userBarChart,
      orderData,
      selectedPeriod,
      loadCharts,
      customStart,
      customEnd,
      searchText,
      filteredCompetitions,
    };
  },
});
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  padding-bottom: 2rem;
}

.date-selector-grid {
  display: grid;
  padding: 1rem;
  grid-template-columns: 1fr;
}

@media (max-width: 800px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 3rem;
}

.report-section h2 {
  padding-top: 2rem;
  padding-bottom: 0.3rem;
  font-size: 1.5em;
  color: #726e6e;
}

.time-period-section {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: var(--clr-primary);
  padding: 1rem 1rem;
  margin-inline: 2rem;
  color: white;
  font-size: 24px;
  border-radius: 3px;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.date-selector select {
  width: 100%;
  padding: 1rem;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background-color: #fff;
}

.date-selector input {
  width: 100%;
  padding: 1rem;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background-color: #fff;
}

.date-selector button {
  color: #fff;
  border-radius: 3px;
  border: none;
  width: 100%;
  cursor: pointer;
  padding: 1rem;

  background-color: #ff4500;
  font-size: 20px;
  margin-top: 1rem;
}

.date-selector button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.time-period-section label {
  font-size: 16px;
}

.date-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  overflow-x: auto;
  position: relative;
  background-color: #fff;
  padding: 80px 40px 40px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -50px;
}

@media (max-width: 950px) {
  .date-selector {
    grid-template-columns: 1fr;
  }
}

section {
  --col-1: #4cff00;
  padding: 4rem 2rem;
}

h1 {
  font-size: 1.8em;
  color: #726e6e;
  margin-bottom: 20px;
}

canvas {
  max-width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 3px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  border: 8px solid #ececec;
}

.filter-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  background-color: #fff;
  padding: 60px 40px 20px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -50px;
  width: 100%;
}

th {
  font-weight: 600;
  text-align: start;
  border-bottom: 1px solid grey;
}

td {
  padding: 0.7rem 0.3rem;
}

tr:nth-child(even) {
  background-color: #cfcfcf4a;
}

.custom-dates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-block: 1rem;
}

canvas {
  max-height: 500px;
}
</style>
